import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { tgLogsService } from '../services/tgLogsService';

const TG_TRANSACTION_TTL_MS = 2 * 60 * 1000;

function getDefaultDateRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: fmt(from), to: fmt(now) };
}

export const useTgTransactionStore = create(
  devtools(
    (set, get) => ({
      logs: [],
      summary: {},
      lastDocId: '',
      totalLogs: 0,
      loading: false,
      timeZone: '',
      lastFetched: 0,
      logType: 'all',
      status: 'all',
      dateRange: getDefaultDateRange(),
      statusCounts: {
        types: { all: 0, sms: 0, call: 0 },
        smsStatuses: {},
        callStatuses: {},
      },
      statusCountsCachedAt: null,
      setTimeZone: (timeZone) => set({ timeZone }, false, 'tgTransaction/setTimeZone'),
      setLogTypeAndStatus: (logType, status) =>
        set({ logType, status: status || 'all' }, false, 'tgTransaction/setLogTypeAndStatus'),
      setDateRange: (from, to) =>
        set({ dateRange: { from, to } }, false, 'tgTransaction/setDateRange'),
      loadStatusCounts: async () => {
        const state = get();
        const { from, to } = state.dateRange;
        const timeZone = state.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        try {
          const response = await tgLogsService.getStatusCounts({ from, to, timezone: timeZone });
          const body = response?.data?.data || response?.data || {};
          const { counts } = body;
          if (counts) {
            set(
              { statusCounts: counts, statusCountsCachedAt: body.cachedAt || Date.now() },
              false,
              'tgTransaction/statusCounts'
            );
          }
          return counts;
        } catch (error) {
          console.error('Error loading status counts:', error);
          return get().statusCounts;
        }
      },
      loadTransactions: async ({ limit, force = false, resetCursor = false } = {}) => {
        const state = get();
        const timeZone = state.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { from, to } = state.dateRange;
        const cacheFresh = Date.now() - state.lastFetched < TG_TRANSACTION_TTL_MS;
        if (!force && state.logs.length && cacheFresh && !resetCursor) {
          return state.logs;
        }

        set({ loading: true }, false, 'tgTransaction/load:start');
        try {
          const [summaryResponse, logsResponse] = await Promise.all([
            tgLogsService.getSummary({ timezone: timeZone, from, to }),
            tgLogsService.getLogs({
              timezone: timeZone,
              limit,
              lastDocId: resetCursor ? '' : state.lastDocId || '',
              logType: state.logType,
              status: state.status,
              from,
              to,
            }),
          ]);
          const summaryPayload = summaryResponse?.data?.summary || summaryResponse?.data?.data?.summary || {};
          const logsPayload = logsResponse?.data?.logs || logsResponse?.data?.data?.logs || [];
          const lastDocIdPayload = logsResponse?.data?.lastDocId || logsResponse?.data?.data?.lastDocId || '';
          const totalLogsPayload = logsResponse?.data?.totalLogs || logsResponse?.data?.data?.totalLogs || 0;
          set(
            {
              summary: summaryPayload,
              logs: logsPayload,
              lastDocId: lastDocIdPayload,
              totalLogs: totalLogsPayload,
              loading: false,
              lastFetched: Date.now(),
              timeZone,
            },
            false,
            'tgTransaction/load:success'
          );
          return logsPayload;
        } catch (error) {
          set({ loading: false }, false, 'tgTransaction/load:error');
          throw error;
        }
      },
      loadMoreTransactions: async ({ limit }) => {
        const state = get();
        const timeZone = state.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        const { from, to } = state.dateRange;
        set({ loading: true }, false, 'tgTransaction/loadMore:start');
        try {
          const response = await tgLogsService.getLogs({
            timezone: timeZone,
            limit,
            lastDocId: state.lastDocId || '',
            logType: state.logType,
            status: state.status,
            from,
            to,
          });
          const logsPayload = response?.data?.logs || response?.data?.data?.logs || [];
          const lastDocIdPayload = response?.data?.lastDocId || response?.data?.data?.lastDocId || state.lastDocId;
          const totalLogsPayload = response?.data?.totalLogs || response?.data?.data?.totalLogs || state.totalLogs;
          set(
            {
              logs: [...state.logs, ...logsPayload],
              lastDocId: lastDocIdPayload,
              totalLogs: totalLogsPayload,
              loading: false,
              lastFetched: Date.now(),
            },
            false,
            'tgTransaction/loadMore:success'
          );
          return logsPayload;
        } catch (error) {
          set({ loading: false }, false, 'tgTransaction/loadMore:error');
          throw error;
        }
      },
      exportLogs: async ({ from, to, timezone }) => tgLogsService.exportLogs({ from, to, timezone }),
    }),
    { name: 'tg-transaction-store' }
  )
);
